import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import { type FillCard, type FillCheck, FillFieldKey } from '@/models/fillApplication'
import { focusFillField } from './focusFillField'

export enum RosterKind {
  DRIVERS = 'drivers',
  TRUCKS = 'trucks',
  TRAILERS = 'trailers',
}

export interface RosterRequest {
  kinds: RosterKind[]
  /** Bumped on every request, so asking twice opens a roster the rep closed in between. */
  nonce: number
}

interface FillJumpValue {
  jump: (check: Pick<FillCheck, 'field' | 'card'>) => void
  /** Brings a card into view without focusing a field, e.g. to show an edit that wouldn't save. */
  showCard: (card: FillCard) => void
  rosterRequest: RosterRequest | null
}

const ROSTER_FIELDS: Partial<Record<string, RosterKind[]>> = {
  [FillFieldKey.DRIVERS]: [RosterKind.DRIVERS],
  [FillFieldKey.DRIVER_ROWS]: [RosterKind.DRIVERS],
  [FillFieldKey.EQUIPMENT]: [RosterKind.TRUCKS, RosterKind.TRAILERS],
  [FillFieldKey.EQUIPMENT_ROWS]: [RosterKind.TRUCKS, RosterKind.TRAILERS],
}

const FillJumpContext = createContext<FillJumpValue>({
  jump: (check) => {
    focusFillField(check.field, check.card)
  },
  showCard: () => undefined,
  rosterRequest: null,
})

interface FillJumpProviderProps {
  /** Brings the card's tab up before focus moves to it. */
  onShowCard?: (card: FillCard) => void
  children: ReactNode
}

export function FillJumpProvider({ onShowCard, children }: FillJumpProviderProps) {
  const [rosterRequest, setRosterRequest] = useState<RosterRequest | null>(null)

  const jump = useCallback(
    (check: Pick<FillCheck, 'field' | 'card'>) => {
      onShowCard?.(check.card)
      const kinds = ROSTER_FIELDS[check.field]
      if (kinds) setRosterRequest((current) => ({ kinds, nonce: (current?.nonce ?? 0) + 1 }))
      // Next tick, so the tab or dialog and any roster opened above have rendered before focus moves. The
      // request is cleared only then: a dialog's portal mounts its rosters a render late, after an effect
      // here would have run. Once read, it's gone, so a roster opened again later starts as it was left.
      window.setTimeout(() => {
        focusFillField(check.field, check.card)
        if (kinds) setRosterRequest(null)
      }, 0)
    },
    [onShowCard]
  )

  const showCard = useCallback((card: FillCard) => onShowCard?.(card), [onShowCard])
  const value = useMemo(() => ({ jump, showCard, rosterRequest }), [jump, showCard, rosterRequest])
  return <FillJumpContext.Provider value={value}>{children}</FillJumpContext.Provider>
}

export function useFillJump(): FillJumpValue {
  return useContext(FillJumpContext)
}
